import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";

const RentalDashboardLayer = () => {
  const [loading, setLoading] = useState(true);
  const [rentals, setRentals] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem("authToken");

        if (!apiUrl || !token) {
          setLoading(false);
          return;
        }

        const [rRes, pRes] = await Promise.all([
          fetch(`${apiUrl}/rentals`, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }),
          fetch(`${apiUrl}/rental-payments`, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }),
        ]);

        const rJson = await rRes.json();
        const pJson = await pRes.json();

        if (rJson?.success && Array.isArray(rJson.data)) {
          setRentals(rJson.data);
        }
        if (pJson?.success && Array.isArray(pJson.data)) {
          setPayments(pJson.data);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load rental dashboard data", e);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const metrics = useMemo(() => {
    const toNumber = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v));
    const totalRentals = rentals.length;
    const totalCurrentRent = rentals.reduce(
      (sum, r) => sum + toNumber(r.current_rent_per_month),
      0
    );

    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const ym = `${y}-${m}`;

    const totalPaymentsThisMonth = payments
      .filter((p) => (p.payment_month || "").startsWith(ym))
      .reduce((sum, p) => sum + toNumber(p.amount_paid), 0);

    const outstandingThisMonth = Math.max(totalCurrentRent - totalPaymentsThisMonth, 0);

    return {
      totalRentals,
      totalCurrentRent,
      totalPaymentsThisMonth,
      outstandingThisMonth,
    };
  }, [rentals, payments]);

  const districtChart = useMemo(() => {
    const counts = new Map();
    rentals.forEach((r) => {
      const d = r.district || "Unknown";
      counts.set(d, (counts.get(d) || 0) + 1);
    });
    const categories = Array.from(counts.keys());
    const series = Array.from(counts.values());
    return { categories, series };
  }, [rentals]);

  const chartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    xaxis: { categories: districtChart.categories },
    plotOptions: { bar: { distributed: true, borderRadius: 4 } },
    dataLabels: { enabled: false },
    colors: ["#4189dd", "#97cbff", "#095593", "#073656", "#22C55E", "#F59E0B", "#EF4444"],
    legend: { show: false },
  };

  return (
    <div className='row gy-4'>
      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-secondary-light mb-8'>Total Rentals</h6>
            <h3 className='mb-0'>{metrics.totalRentals}</h3>
          </div>
        </div>
      </div>
      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-secondary-light mb-8'>Total Current Monthly Rent</h6>
            <h3 className='mb-0'>{metrics.totalCurrentRent.toLocaleString()}</h3>
          </div>
        </div>
      </div>
      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-secondary-light mb-8'>Payments This Month</h6>
            <h3 className='mb-0'>{metrics.totalPaymentsThisMonth.toLocaleString()}</h3>
          </div>
        </div>
      </div>
      <div className='col-xxl-3 col-md-6'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-secondary-light mb-8'>Outstanding This Month</h6>
            <h3 className='mb-0'>{metrics.outstandingThisMonth.toLocaleString()}</h3>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='mb-20 fw-bold text-lg'>Rentals by District</h6>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : districtChart.series.length > 0 ? (
              <ReactApexChart options={chartOptions} series={[{ data: districtChart.series }]} type='bar' height={360} />
            ) : (
              <div className='text-center py-5 text-secondary-light'>No rental data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalDashboardLayer;


